import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Star, TrendingUp, Activity, LineChart, Radar, Flame, Grid } from 'lucide-react';
import CoinSearch from '../components/crypto/CoinSearch';
import ChartCanvas from '../components/crypto/ChartCanvas';
import ChartToolbar from '../components/crypto/ChartToolbar';
import Watchlist from '../components/crypto/Watchlist';
import CoinInfo from '../components/crypto/CoinInfo';
import NewsPanel from '../components/crypto/NewsPanel';
import IndicatorPane from '../components/crypto/IndicatorPane';
import DrawingOverlay from '../components/crypto/DrawingOverlay';
import AIAnalysisPanel from '../components/crypto/AIAnalysisPanel';
import AlertsPanel from '../components/crypto/AlertsPanel';
import StructuredAIPanel from '../components/crypto/StructuredAIPanel';
import MultiTimeframeSummary from '../components/crypto/MultiTimeframeSummary';
import RegimeOverlay from '../components/crypto/RegimeOverlay';
import SessionOverlay from '../components/crypto/SessionOverlay';
import BandFillOverlay from '../components/crypto/BandFillOverlay';
import PlaybackBar from '../components/crypto/PlaybackBar';
import IndicatorColorEditor from '../components/crypto/IndicatorColorEditor';
import MarketScanner from '../components/crypto/MarketScanner';
import Heatmap from '../components/crypto/Heatmap';
import MultiChart from '../components/crypto/MultiChart';
import MobileSheet from '../components/MobileSheet';
import { List, Info, X } from 'lucide-react';
import { makeDecisionZonesPlugin } from '../lib/crypto/indicators/decisionZones';
import { useBinanceKline } from '../lib/crypto/useBinanceStream';

// In-memory candle cache — switching symbols paints instantly from
// cache while a fresh fetch runs in the background. Cache TTL: 60s.
const _CANDLE_CACHE = new Map();  // key: `${sym}|${tf}` → { at, rows }
const _CACHE_TTL = 60_000;

/**
 * Crypto workspace — Phase 52.A.
 *
 * Learning isolation: reads live market data from Binance.US +
 * CoinGecko; writes only to the crypto_watchlist / crypto_prefs
 * collections. The Trading Engine (learning, truth, discipline) is
 * not touched.
 */
export default function Crypto() {
  const [prefs, setPrefs] = useState(null);          // fetched
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [base, setBase] = useState('BTC');
  const [quote, setQuote] = useState('USDT');
  const [candles, setCandles] = useState([]);
  const [loadingCandles, setLoadingCandles] = useState(false);
  const [globals, setGlobals] = useState(null);
  const [wlSyms, setWlSyms] = useState([]);          // list of symbols
  const [wlTick, setWlTick] = useState(0);           // bump to force reload
  const [engine, setEngine] = useState(null);       // ChartEngine ref for drawing overlay
  const [crosshairTime, setCrosshairTime] = useState(null);   // Sync crosshair main<->panes (G2 B.2)
  const loadingOlderRef = useRef(false);                       // Guard against duplicate history-page fetches (G2 B.1)
  const [alertBadge, setAlertBadge] = useState(0);  // count of newly-fired alerts
  const [analysis, setAnalysis] = useState(null);   // structured AI analysis (Phase 53.A)
  const [view, setView] = useState('chart');        // chart | scanner | heatmap | multi
  const [panesHeight, setPanesHeight] = useState(360); // draggable sub-panes area height (px)
  const draggingRef = useRef(false);
  // Visible time range from the main chart — sub-panes slice their
  // candles to match so their X-axis lines up bar-for-bar.
  const [visibleRange, setVisibleRange] = useState(null);
  // ── Playback / Replay mode (Phase 52.1 Next+) ────────────────────
  // When active, `chartCandles` is a growing slice of `candles` so
  // every indicator paints only history up to `playbackIdx`.
  const [playbackActive, setPlaybackActive] = useState(false);
  const [playbackIdx, setPlaybackIdx] = useState(0);
  useEffect(() => {
    // On entering playback, seed idx to the last candle so the chart
    // looks unchanged, then let the operator scrub backwards.
    if (playbackActive && candles?.length) {
      setPlaybackIdx((cur) => (cur > 0 ? cur : candles.length));
    }
  }, [playbackActive, candles?.length]);
  const chartCandles = React.useMemo(() => {
    if (!playbackActive || !candles?.length) return candles;
    return candles.slice(0, Math.max(1, Math.min(candles.length, playbackIdx)));
  }, [playbackActive, playbackIdx, candles]);
  // ── DBB / KC / VWAP colour customisation modal state ─────────────
  const [colorsOpen, setColorsOpen] = useState(false);
  // ── Mobile drawers for Watchlist + Right Info column ────────────
  const [mobileDrawer, setMobileDrawer] = useState(null); // null | 'watchlist' | 'info'

  // ── Prefs load / save ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/crypto/prefs');
        setPrefs(data);
        if (data?.last_symbol) {
          setSymbol(data.last_symbol);
          setBase(data.last_symbol.replace(/USDT|USDC|USD$/, ''));
          setQuote(data.last_symbol.endsWith('USDC') ? 'USDC' :
                   data.last_symbol.endsWith('USD') ? 'USD' : 'USDT');
        }
      } catch { setPrefs({ chart_type: 'candles', timeframe: '1h',
          indicators: { ema20: true, ema50: true, ema200: true, rsi: true, vwap: false },
          last_symbol: 'BTCUSDT' }); }
    })();
    (async () => {
      try {
        const { data } = await api.get('/crypto/globals');
        setGlobals(data);
      } catch { /* noop */ }
    })();
  }, []);

  // ── Watchlist symbol list (used to render the favorite star in CoinInfo)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/crypto/watchlist');
        setWlSyms((data?.items || []).map((i) => i.symbol));
      } catch { /* noop */ }
    })();
  }, [wlTick]);

  // ── OHLC loader — triggered on symbol / timeframe changes ─────────
  // Warms from an in-memory cache for instant paint, then refreshes
  // from the backend in the background. Full-refresh interval is
  // 60s (WS handles the live edges).
  useEffect(() => {
    if (!prefs) return;
    let cancelled = false;
    const key = `${symbol}|${prefs.timeframe}`;
    const cached = _CANDLE_CACHE.get(key);
    if (cached?.rows?.length) {
      // Paint immediately from cache.
      setCandles(cached.rows);
      setLoadingCandles(false);
    } else {
      setLoadingCandles(true);
    }
    (async () => {
      try {
        const { data } = await api.get(`/crypto/ohlc/${symbol}`, {
          params: { tf: prefs.timeframe, limit: 1000 },
        });
        const rows = data?.candles || [];
        if (!cancelled) {
          setCandles(rows);
          _CANDLE_CACHE.set(key, { at: Date.now(), rows });
        }
      } catch { if (!cancelled && !cached) setCandles([]); }
      finally { if (!cancelled) setLoadingCandles(false); }
    })();
    // Belt-and-suspenders: refresh the last few candles via REST
    // every 3s (down from 60s). The Binance kline WS handles the
    // silky tick-by-tick updates when it's healthy, but if the
    // socket ever silently stalls (mobile background tab, firewall,
    // Binance regional outage) the chart still ticks visibly.
    // 3 s is well under the 20 s kline cache TTL so it costs one
    // upstream call/minute at most (cached response otherwise).
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/crypto/ohlc/${symbol}`, {
          params: { tf: prefs.timeframe, limit: 60 },
        });
        const rows = data?.candles || [];
        if (cancelled || !rows.length) return;
        setCandles((prev) => {
          if (!prev?.length) return rows;
          // Detect a stalled WS: if we haven't seen a live kline
          // tick in >5 s AND prev's tail is a bar NEWER than any
          // REST row (i.e. a stale WS-inserted placeholder), drop
          // it so the fresh REST tail becomes authoritative. Fixes
          // the "chart-ohlc frozen while EMAs drift" symptom from
          // iter 78 RCA.
          const restLatest = rows[rows.length - 1]?.time || 0;
          const wsAgeMs = Date.now() - (lastWsAtRef.current || 0);
          let effectivePrev = prev;
          if (wsAgeMs > 5000 && prev[prev.length - 1]?.time > restLatest) {
            effectivePrev = prev.slice(0, -1);
          }
          // Merge by time (Map dedupes) so any collision picks the
          // FRESHEST row. Sort ascending so tail is the newest bar.
          const byTime = new Map();
          for (const c of effectivePrev) byTime.set(c.time, c);
          for (const r of rows) byTime.set(r.time, r);   // fresh wins
          const merged = [...byTime.values()].sort((a, b) => a.time - b.time);
          return merged.length > 1000 ? merged.slice(merged.length - 1000) : merged;
        });
      } catch { /* noop — the WS + next poll will catch up */ }
    }, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [symbol, prefs?.timeframe, prefs]);

  // ── Background history prefetch (Phase 52.1 G3 perf) ────────────
  // As soon as the initial candle window lands we kick off a single
  // fetch for the *previous* 500-bar page in the background. The
  // response gets tucked into the historical cache so the user's
  // first left-scroll (which triggers `loadOlder`) resolves from the
  // cache instantly — no perceived spinner, no network wait. This
  // costs one extra request per symbol/timeframe visit but the
  // 24 h historical cache keeps it warm across sessions.
  const prefetchedRef = useRef(new Set());
  useEffect(() => {
    if (!candles?.length || !prefs?.timeframe) return;
    const first = candles[0];
    if (!first) return;
    const anchor = first.time - 1;
    const key = `${symbol}|${prefs.timeframe}|${anchor}`;
    if (prefetchedRef.current.has(key)) return;
    prefetchedRef.current.add(key);
    // Fire-and-forget; failure is fine, the on-demand loadOlder
    // path still handles cache misses transparently.
    api.get(`/crypto/ohlc/${symbol}`, {
      params: { tf: prefs.timeframe, limit: 500, end_ts: anchor },
    }).catch(() => { /* silent */ });
  }, [candles, symbol, prefs?.timeframe]);

  // ── Live Binance kline WebSocket — the authoritative OHLC source.
  // We intentionally do NOT overlay Coinbase trades onto the candles;
  // BTC-USD (Coinbase) and BTCUSDT (Binance) are two different order
  // books and can diverge by several dollars, which produced hybrid
  // candles that occasionally moved opposite to both exchanges.
  // Live Binance kline WS timestamp — used by the safety-net REST
  // poll to detect a stalled WS. If we haven't seen a WS tick in >5s
  // we drop any placeholder tail candle before merging fresh REST
  // rows, so the chart-ohlc header always reflects real trade data.
  const lastWsAtRef = useRef(0);
  const liveKline = useBinanceKline(symbol, prefs?.timeframe);
  useEffect(() => {
    if (!liveKline) return;
    lastWsAtRef.current = Date.now();
    setCandles((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      const next = [...prev];
      if (last.time === liveKline.time) {
        next[next.length - 1] = { ...last, ...liveKline };
      } else if (liveKline.time > last.time) {
        next.push(liveKline);
        if (next.length > 1000) next.shift();
      }
      return next;
    });
    // Bump the cache so a symbol/timeframe swap doesn't repaint a
    // stale candle from the previous refresh.
    if (prefs?.timeframe) {
      const key = `${symbol}|${prefs.timeframe}`;
      const cached = _CANDLE_CACHE.get(key);
      if (cached?.rows?.length) {
        const rows = cached.rows;
        const idx = rows.length - 1;
        if (rows[idx]?.time === liveKline.time) rows[idx] = { ...rows[idx], ...liveKline };
      }
    }
  }, [liveKline, symbol, prefs?.timeframe]);

  // ── High-frequency Coinbase trade tick — DISABLED for candle
  // patching in Phase 52.1. Binance.US kline WS is the authoritative
  // OHLC source; snapping Coinbase's BTC-USD price onto a Binance
  // BTCUSDT candle produced hybrid values that occasionally moved
  // opposite to both source exchanges. The kline WS already emits
  // every ~1s per bar, which is plenty for a professional chart.
  //
  // Keep the hook available for CoinInfo / last-price displays where
  // an ultra-live number matters — but never mutate the chart data.
  // (No candle-patch effect on `liveTrade` — intentional.)

  // ── Save prefs on change (debounced) ──────────────────────────────
  useEffect(() => {
    if (!prefs) return;
    const t = setTimeout(async () => {
      try {
        await api.put('/crypto/prefs', { prefs: { ...prefs, last_symbol: symbol } });
      } catch { /* noop */ }
    }, 500);
    return () => clearTimeout(t);
  }, [prefs, symbol]);

  const pick = (row) => {
    setSymbol(row.symbol);
    setBase(row.base);
    setQuote(row.quote);
  };

  // ── Infinite historical scroll (G2 B.1) ─────────────────────────
  // Fires when the ChartCanvas detects the user scrolled within
  // ~20 bars of the leftmost loaded candle. Fetches an older 500-bar
  // page ending at (firstCandleTime - 1s) and prepends the fresh
  // history to the candle state. A ref guards duplicate requests.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (!candles?.length || !prefs?.timeframe) return;
    loadingOlderRef.current = true;
    try {
      const first = candles[0];
      const { data } = await api.get(`/crypto/ohlc/${symbol}`, {
        params: { tf: prefs.timeframe, limit: 500, end_ts: first.time - 1 },
      });
      const older = data?.candles || [];
      if (!older.length) return;
      setCandles((prev) => {
        if (!prev.length) return older;
        // De-dupe on the join seam (Binance's `endTime` is inclusive
        // of the anchor bar, so the last row of `older` might overlap
        // the first row of `prev`).
        const cutoff = prev[0].time;
        const merged = older.filter((c) => c.time < cutoff);
        return merged.length ? [...merged, ...prev] : prev;
      });
    } catch { /* silent — will retry on next scroll edge */ }
    finally { loadingOlderRef.current = false; }
  }, [candles, prefs?.timeframe, symbol]);

  const toggleWatch = async () => {
    try {
      const { data } = await api.post('/crypto/watchlist/toggle', {
        symbol, base, quote,
      });
      setWlSyms((data?.items || []).map((i) => i.symbol));
      setWlTick((t) => t + 1);
      toast.success(data?.action === 'added' ? `${base} added to watchlist` : `${base} removed`);
    } catch (e) {
      toast.error('Watchlist toggle failed');
    }
  };

  if (!prefs) {
    return (
      <div className="p-4 text-[10px] text-[var(--txt-muted)] tracking-widest uppercase">// loading crypto workspace…</div>
    );
  }

  const isFav = wlSyms.includes(symbol);

  const VIEWS = [
    { key: 'chart',    label: 'Chart',    icon: LineChart },
    { key: 'scanner',  label: 'Scanner',  icon: Radar },
    { key: 'heatmap',  label: 'Heatmap',  icon: Flame },
    { key: 'multi',    label: 'Multi',    icon: Grid },
  ];

  return (
    <div className="flex flex-col lg:h-[calc(100vh-2.5rem)] lg:overflow-hidden" data-testid="crypto-page">
      {/* Top bar — search + view tabs + globals summary */}
      <div className="flex items-center gap-3 border-b border-[var(--border-default)] bg-[var(--bg-panel)] px-3 py-2 flex-wrap">
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys">// crypto workspace</div>
        <div className="flex items-center gap-1">
          {VIEWS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              data-testid={`crypto-view-${key}`}
              className={`px-2 py-1 border text-[9px] tracking-widest uppercase flex items-center gap-1 ${view === key ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-cyan-sys'}`}
            >
              <Icon size={10} /> {label}
            </button>
          ))}
        </div>
        {view === 'chart' && (
          <div className="min-w-[240px] flex-1 max-w-md">
            <CoinSearch onPick={pick} current={base} />
          </div>
        )}
        <div className="flex items-center gap-3 text-[10px] font-mono ml-auto">
          <div>
            <span className="text-[var(--txt-muted)]">BTC DOM</span>{' '}
            <span className="text-cyan-sys tabular-nums">
              {globals?.btc_dominance != null ? `${globals.btc_dominance.toFixed(2)}%` : '—'}
            </span>
          </div>
          <div>
            <span className="text-[var(--txt-muted)]">TOTAL MCAP</span>{' '}
            <span className="text-cyan-sys tabular-nums">
              {globals?.total_market_cap_usd
                ? `$${(globals.total_market_cap_usd / 1e12).toFixed(2)}T`
                : '—'}
            </span>
          </div>
          <div>
            <span className="text-[var(--txt-muted)]">F&amp;G</span>{' '}
            <span
              className={`tabular-nums ${globals?.fear_and_greed?.value >= 70 ? 'text-buy'
                : globals?.fear_and_greed?.value <= 30 ? 'text-sell' : 'text-hold'}`}
              data-testid="crypto-fng"
            >
              {globals?.fear_and_greed?.value ?? '—'}
              {globals?.fear_and_greed?.label && ` · ${globals.fear_and_greed.label}`}
            </span>
          </div>
        </div>
      </div>

      {/* Body — swap by view */}
      {view === 'scanner' && (
        <div className="flex-1 min-h-0 lg:min-h-0 min-h-[80vh]">
          <MarketScanner
            onPick={(row) => { pick(row); setView('chart'); }}
          />
        </div>
      )}
      {view === 'heatmap' && (
        <div className="flex-1 min-h-0 lg:min-h-0 min-h-[80vh]">
          <Heatmap
            onPick={(row) => { pick(row); setView('chart'); }}
          />
        </div>
      )}
      {view === 'multi' && (
        <div className="flex-1 min-h-0 lg:min-h-0 min-h-[80vh]">
          <MultiChart />
        </div>
      )}
      {view === 'chart' && (
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[220px_1fr_320px] min-h-0">
        {/* Left — Watchlist (desktop only, mobile uses drawer) */}
        <aside className="border-r border-[var(--border-default)] bg-black min-h-0 hidden lg:block" data-testid="crypto-watchlist-aside">
          <Watchlist current={symbol} onPick={pick} refreshTick={wlTick} />
        </aside>

        {/* Mobile-only quick action bar: opens Watchlist / Info drawers */}
        <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b border-[var(--border-default)] bg-[var(--bg-panel)]">
          <button
            onClick={() => setMobileDrawer('watchlist')}
            data-testid="crypto-mobile-watchlist-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 border border-cyan-sys text-cyan-sys text-[10px] tracking-widest uppercase py-2 min-h-[36px]"
          >
            <List size={12} /> watchlist
            {wlSyms.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[9px] px-1 bg-cyan-sys text-black rounded-full">
                {wlSyms.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMobileDrawer('info')}
            data-testid="crypto-mobile-info-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 border border-cyan-sys text-cyan-sys text-[10px] tracking-widest uppercase py-2 min-h-[36px]"
          >
            <Info size={12} /> coin info / AI
            {alertBadge > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[9px] px-1 bg-sell text-black rounded-full">
                {alertBadge}
              </span>
            )}
          </button>
        </div>

        {/* Center — Chart + Toolbar + RSI */}
        <section className="flex flex-col min-h-[65vh] lg:min-h-0 overflow-hidden border-r border-[var(--border-default)]">
          <ChartToolbar
            prefs={{ ...prefs, playback_active: playbackActive }}
            onPrefs={setPrefs}
            onResetScale={() => {
              const chart = engine?.provider?.chart;
              if (!chart) return;
              try { chart.priceScale('right').applyOptions({ autoScale: true }); } catch { /* noop */ }
              try { chart.timeScale().fitContent(); } catch { /* noop */ }
            }}
            onOpenColors={() => setColorsOpen(true)}
            onTogglePlayback={() => setPlaybackActive((p) => !p)}
          />
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-[var(--border-default)] bg-black">
            <div className="flex items-baseline gap-3">
              <div className="text-[13px] font-mono text-cyan-sys tracking-tight" data-testid="crypto-current-symbol">
                {base}/{quote}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">
                {prefs.timeframe} · {prefs.chart_type}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleWatch}
                data-testid="crypto-add-watch"
                className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] tracking-widest border ${isFav
                  ? 'border-hold text-hold' : 'border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black'}`}
              >
                <Star size={10} fill={isFav ? 'currentColor' : 'none'} /> {isFav ? 'in watchlist' : 'add to watchlist'}
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 relative">
            {loadingCandles && candles.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-[10px] text-cyan-sys uppercase tracking-widest">
                // fetching {prefs.timeframe} candles…
              </div>
            )}
            <ChartCanvas
              candles={chartCandles}
              type={prefs.chart_type}
              pan2d={!!prefs.pan2d}
              indicators={{
                ema9:       !!prefs.indicators?.ema9,
                ema20:      !!prefs.indicators?.ema20,
                ema21:      !!prefs.indicators?.ema21,
                ema50:      !!prefs.indicators?.ema50,
                ema100:     !!prefs.indicators?.ema100,
                ema200:     !!prefs.indicators?.ema200,
                ema300:     !!prefs.indicators?.ema300,
                ema500:     !!prefs.indicators?.ema500,
                vwap:       !!prefs.indicators?.vwap,
                bb:         !!prefs.indicators?.bb,
                dbb:        !!prefs.indicators?.dbb,
                kc:         !!prefs.indicators?.kc,
                supertrend: !!prefs.indicators?.supertrend,
              }}
              indicatorColors={prefs.indicator_colors || null}
              onEngineReady={setEngine}
              onVisibleRangeChange={setVisibleRange}
              onCrosshairMove={setCrosshairTime}
              onLoadOlder={loadOlder}
              zones={(prefs.ai_overlay !== false) ? (analysis?.zones || null) : null}
            />
            {engine && (
              <>
                <RegimeOverlay engine={engine} segments={prefs.regime_shading ? (analysis?._regime_segments || []) : []} />
                <SessionOverlay engine={engine} enabled={!!prefs.session_overlay} />
                <BandFillOverlay
                  engine={engine}
                  candles={chartCandles}
                  indicators={{ dbb: !!prefs.indicators?.dbb, bb: !!prefs.indicators?.bb }}
                  colors={prefs.indicator_colors || null}
                />
                <DrawingOverlay engine={engine} symbol={symbol} timeframe={prefs.timeframe} />
              </>
            )}
          </div>
          {playbackActive && (
            <PlaybackBar
              total={candles?.length || 0}
              idx={playbackIdx}
              onIdxChange={(next) => setPlaybackIdx((cur) => (typeof next === 'function' ? next(cur) : next))}
              onClose={() => { setPlaybackActive(false); setPlaybackIdx(0); }}
            />
          )}
          {/* Sub-indicator panes — a small drag handle sits between the
             chart and this container so the user can drop it up/down
             to make either side bigger, TradingView-style. Candle/line
             widths automatically adjust because the main chart is
             `flex-1` above. */}
          {(prefs.indicators?.rsi || prefs.indicators?.macd || prefs.indicators?.atr || prefs.indicators?.adx || prefs.indicators?.rvol) && (() => {
            const activeCount = ['rsi', 'macd', 'atr', 'adx', 'rvol']
              .filter((k) => prefs.indicators?.[k]).length;
            // Aim for ~90px per pane so integer Y-axis labels
            // (RVOL 0..6+, ADX 0/25/50/75/100 etc) don't visually
            // overlap. Never let a pane drop below 60px.
            const perPane = Math.max(60, Math.floor(panesHeight / activeCount));
            const onSplitDown = (e) => {
              e.preventDefault();
              draggingRef.current = { startY: e.clientY, startH: panesHeight };
              const onMove = (ev) => {
                if (!draggingRef.current) return;
                const dy = ev.clientY - draggingRef.current.startY;
                // Drag UP (dy negative) → sub-panes grow.
                const next = Math.max(80, Math.min(600, draggingRef.current.startH - dy));
                setPanesHeight(next);
              };
              const onUp = () => {
                draggingRef.current = false;
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            };
            return (
              <>
                <div
                  onMouseDown={onSplitDown}
                  data-testid="crypto-pane-split"
                  title="Drag to resize the indicator panes"
                  className="group h-2.5 bg-[var(--bg-panel)] hover:bg-cyan-sys/25 cursor-ns-resize flex-shrink-0 transition-colors flex items-center justify-center border-y border-[var(--border-default)] relative z-10 select-none"
                >
                  {/* Grip dots — mimic TradingView's separator handle so
                      the drag affordance is discoverable at a glance. */}
                  <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <span className="w-0.5 h-0.5 rounded-full bg-cyan-sys" />
                    <span className="w-0.5 h-0.5 rounded-full bg-cyan-sys" />
                    <span className="w-0.5 h-0.5 rounded-full bg-cyan-sys" />
                    <span className="w-0.5 h-0.5 rounded-full bg-cyan-sys" />
                    <span className="w-0.5 h-0.5 rounded-full bg-cyan-sys" />
                  </div>
                </div>
                <div
                  className="flex-shrink-0 flex flex-col"
                  style={{ height: `${panesHeight}px` }}
                >
                  {prefs.indicators?.rsi  && <IndicatorPane type="rsi"  candles={chartCandles} height={perPane} visibleRange={visibleRange} engine={engine} crosshairTime={crosshairTime} onHoverTime={setCrosshairTime} />}
                  {prefs.indicators?.macd && <IndicatorPane type="macd" candles={chartCandles} height={perPane} visibleRange={visibleRange} engine={engine} crosshairTime={crosshairTime} onHoverTime={setCrosshairTime} />}
                  {prefs.indicators?.atr  && <IndicatorPane type="atr"  candles={chartCandles} height={perPane} visibleRange={visibleRange} engine={engine} crosshairTime={crosshairTime} onHoverTime={setCrosshairTime} />}
                  {prefs.indicators?.adx  && <IndicatorPane type="adx"  candles={chartCandles} height={perPane} visibleRange={visibleRange} engine={engine} crosshairTime={crosshairTime} onHoverTime={setCrosshairTime} />}
                  {prefs.indicators?.rvol && <IndicatorPane type="rvol" candles={chartCandles} height={perPane} visibleRange={visibleRange} engine={engine} crosshairTime={crosshairTime} onHoverTime={setCrosshairTime} />}
                </div>
              </>
            );
          })()}
        </section>

        {/* Right — Coin info + AI + Alerts + News */}
        <aside className="flex flex-col min-h-0 overflow-y-auto p-2 gap-2 bg-black hidden lg:flex">
          <CoinInfo
            symbol={symbol}
            base={base}
            quote={quote}
            isFav={isFav}
            onToggleFav={toggleWatch}
          />
          <StructuredAIPanel symbol={symbol} timeframe={prefs.timeframe} onAnalysis={setAnalysis} />
          <MultiTimeframeSummary symbol={symbol} />
          <AIAnalysisPanel symbol={symbol} timeframe={prefs.timeframe} />
          <AlertsPanel symbol={symbol} onBadgeChange={setAlertBadge} />
          <NewsPanel base={base} />
        </aside>
      </div>
      )}
      {/* Phase 52.1 Next+ — Indicator colour palette modal */}
      <IndicatorColorEditor
        open={colorsOpen}
        initial={prefs.indicator_colors}
        onClose={() => setColorsOpen(false)}
        onApply={(next) => { setPrefs({ ...prefs, indicator_colors: next }); setColorsOpen(false); }}
        onReset={() => setPrefs({ ...prefs, indicator_colors: null })}
      />

      {/* Mobile drawers — Watchlist + Right Info column */}
      <MobileSheet
        open={mobileDrawer === 'watchlist'}
        onClose={() => setMobileDrawer(null)}
        title="// Watchlist"
        testId="crypto-mobile-watchlist-sheet"
      >
        <div className="min-h-[50vh]">
          <Watchlist
            current={symbol}
            onPick={(row) => { pick(row); setMobileDrawer(null); }}
            refreshTick={wlTick}
          />
        </div>
      </MobileSheet>
      <MobileSheet
        open={mobileDrawer === 'info'}
        onClose={() => setMobileDrawer(null)}
        title={`// ${base}/${quote} · Coin Info + AI`}
        testId="crypto-mobile-info-sheet"
      >
        <div className="flex flex-col gap-3">
          <CoinInfo
            symbol={symbol}
            base={base}
            quote={quote}
            isFav={isFav}
            onToggleFav={toggleWatch}
          />
          <StructuredAIPanel symbol={symbol} timeframe={prefs.timeframe} onAnalysis={setAnalysis} />
          <MultiTimeframeSummary symbol={symbol} />
          <AIAnalysisPanel symbol={symbol} timeframe={prefs.timeframe} />
          <AlertsPanel symbol={symbol} onBadgeChange={setAlertBadge} />
          <NewsPanel base={base} />
        </div>
      </MobileSheet>
    </div>
  );
}
